"use client";

import { Loader2, Trash } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Furigana } from "~/components/common/Furigana";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { formatDuration } from "~/lib/utils";
import { JLPT_TEXT_WEIGHTS } from "~/server/db/videos/constants";
import { type SelectVideoWithTranslationsAndUsers } from "~/server/db/videos/schema";

import ToggleFavorite from "./[videoId]/controls/ToggleFavorite";
import ToggleIsStudying from "./[videoId]/controls/ToggleIsStudying";

interface VideoCardProps {
  video: SelectVideoWithTranslationsAndUsers;
  onDelete: (formData: FormData) => void;
  isPending: boolean;
  discover?: boolean;
}

const formatPublishedDate = (date: Date | string | null | undefined) => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString();
};

export function VideoCard({
  video: {
    id: videoId,
    title,
    translations,
    publishedOn,
    duration,
    summary,
    thumbnailUrl,
    users,
    jlptLevel,
    isImportCompleted,
  },
  discover,
  onDelete,
  isPending,
}: VideoCardProps) {
  const videoUser = users?.[0];
  const isFavorite = videoUser?.isFavorite ?? false;
  const isStudying = videoUser?.isStudying ?? false;
  const translation = translations?.[0];
  const allowDelete = !discover && !!videoUser;

  return (
    <Card
      className="flex h-full flex-col overflow-hidden hover:shadow-lg"
      key={videoId}
    >
      <Link href={`/videos/${videoId}#top`} className="block flex-grow">
        <CardContent className="flex-shrink-0 p-0">
          <div className="relative aspect-video">
            {thumbnailUrl && (
              <Image
                src={thumbnailUrl}
                alt={title}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                className="object-cover"
              />
            )}
          </div>
        </CardContent>
        <CardHeader className="min-h-[220px] flex-grow">
          <CardTitle className="mb-0 min-h-[4em] overflow-hidden pb-0 text-xl">
            <div className="[line-height:1.2]">
              <Furigana
                rubyContent={summary ?? ""}
                className="block leading-normal"
              />
            </div>
          </CardTitle>
          <CardDescription className="line-clamp-3 pt-2">
            {translation?.summary}
          </CardDescription>
        </CardHeader>
      </Link>
      <CardFooter className="grid grid-cols-3 items-center border-t bg-muted/50 px-3 py-3">
        <div className="text-xs text-muted-foreground">
          <div>{formatPublishedDate(publishedOn)}</div>
          <div>{formatDuration(duration)}</div>
        </div>

        <div className="flex justify-center text-sm">
          {jlptLevel > 0 && (
            <span
              className={
                JLPT_TEXT_WEIGHTS[jlptLevel as keyof typeof JLPT_TEXT_WEIGHTS]
              }
            >
              N{jlptLevel}
            </span>
          )}
        </div>

        <div className="flex justify-end gap-1">
          <ToggleFavorite videoId={videoId} defaultIsFavorite={isFavorite} />
          <ToggleIsStudying
            videoId={videoId}
            defaultIsStudying={isStudying}
            isImportCompleted={isImportCompleted}
          />
          {allowDelete && (
            <form action={onDelete} className="flex items-center">
              <input type="hidden" name="videoId" value={videoId} />
              <Button
                size="sm"
                variant="ghost"
                disabled={isPending}
                className="h-8 w-8 p-0"
              >
                {isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash className="h-4 w-4" />
                )}
              </Button>
            </form>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
